/* Progressive enhancement for the custom header; native cart and account controls stay untouched. */
(() => {
  if (window.malviceNavigationReady) return;
  window.malviceNavigationReady = true;
  const instances = new Map();
  const focusables = root => [...root.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),summary,[tabindex="0"]')]
    .filter(node => node.getClientRects().length && !node.closest('[hidden]'));
  const initialize = root => {
    root.querySelectorAll('.mv-header-shell').forEach(shell => {
      if (instances.has(shell)) return;
      const controller = new AbortController();
      const options = { signal: controller.signal };
      const menu = shell.querySelector('.mv-mobile-menu');
      const panel = shell.querySelector('.mv-mobile-menu__panel');
      if (!menu || !panel) return;
      const summary = menu.querySelector(':scope > summary');
      const closeMenu = (restore = false) => {
        menu.open = false;
        document.body.classList.remove('mv-menu-open');
        if (restore && summary.getClientRects().length) summary.focus();
      };
      menu.addEventListener('toggle', () => {
        document.body.classList.toggle('mv-menu-open', menu.open);
        if (menu.open) {
          requestAnimationFrame(() => { if (menu.open) (focusables(panel)[0] || panel).focus(); });
        }
      }, options);
      menu.addEventListener('click', event => {
        if (event.target.closest('[data-mv-menu-close]')) closeMenu(true);
        else if (event.target === menu && menu.open) closeMenu(true);
        else if (event.target.closest('.mv-mobile-menu__panel a[href]')) closeMenu();
      }, options);
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          if (menu.open) { event.preventDefault(); closeMenu(true); }
        }
        if (event.key !== 'Tab' || !menu.open) return;
        const items = focusables(panel);
        if (!items.length) { event.preventDefault(); panel.focus(); return; }
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
          event.preventDefault(); first.focus();
        }
      }, options);
      const desktop = matchMedia('(min-width: 1121px)');
      desktop.addEventListener('change', event => { if (event.matches) closeMenu(); }, options);
      instances.set(shell, () => { controller.abort(); closeMenu(); });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [shell, dispose] of instances) {
      if (event.target.contains(shell)) { dispose(); instances.delete(shell); }
    }
  });
})();

(() => {
  const elementName = 'malvice-announcement-rotator';
  if (customElements.get(elementName)) return;

  customElements.define(elementName, class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.items = [...this.querySelectorAll('[data-mv-announcement-item]')];
      this.currentIndex = Math.max(0, this.items.findIndex(item => !item.hidden));
      this.interval = Math.max(3000, Number(this.dataset.interval) || 5000);
      this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
      this.handlePointerEnter = () => this.stop();
      this.handlePointerLeave = () => this.start();
      this.handleFocusIn = () => this.stop();
      this.handleFocusOut = () => {
        requestAnimationFrame(() => {
          if (!this.matches(':focus-within')) this.start();
        });
      };
      this.handleVisibilityChange = () => document.hidden ? this.stop() : this.start();
      this.handleMotionChange = () => this.motionPreference.matches ? this.stop() : this.start();

      this.addEventListener('pointerenter', this.handlePointerEnter);
      this.addEventListener('pointerleave', this.handlePointerLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.motionPreference.addEventListener?.('change', this.handleMotionChange);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      clearTimeout(this.transitionTimer);
      this.removeEventListener('pointerenter', this.handlePointerEnter);
      this.removeEventListener('pointerleave', this.handlePointerLeave);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.motionPreference?.removeEventListener?.('change', this.handleMotionChange);
      this.initialized = false;
    }

    start() {
      this.stop();
      if (this.items.length < 2 || document.hidden || this.motionPreference.matches || this.matches(':hover, :focus-within')) return;
      this.rotationTimer = setInterval(() => this.showNext(), this.interval);
    }

    stop() {
      clearInterval(this.rotationTimer);
      this.rotationTimer = null;
    }

    showNext() {
      if (this.items.length < 2) return;
      const current = this.items[this.currentIndex];
      const nextIndex = (this.currentIndex + 1) % this.items.length;
      const next = this.items[nextIndex];

      clearTimeout(this.transitionTimer);
      current.classList.add('is-leaving');
      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
      if (current.matches('a, button')) current.setAttribute('tabindex', '-1');
      next.hidden = false;
      next.removeAttribute('aria-hidden');
      if (next.matches('a, button')) next.removeAttribute('tabindex');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => next.classList.add('is-active'));
      });

      this.transitionTimer = setTimeout(() => {
        current.hidden = true;
        current.classList.remove('is-leaving');
      }, 340);
      this.currentIndex = nextIndex;
    }
  });
})();
